import {
    AbacoWebModule,
    ApplicationReadyEvent,
} from "@abaco/web-common/src/app"; //RouterReadyEvent, FromStore
import myRoutes from "./routes";
import globals from "./globals";
import { Business } from "./models/Business";
import { MarkerCategories } from "./models/MarkerCategories";
import { HTTPClient, propsToDatePaged } from "@abaco/web-common/src/HTTP"; //propsToDatePaged
import {
    SearchMarkerResult,
    SearchMarkerFilters,
} from "./models/SearchMarkerResult";
import GeoTaggingVuexModule from "./state";
import { PagingParams, PagedResults } from "@abaco/web-common/src/Paging"; //EmptyPagedResults
import { MarkerDetails } from "./models/MarkerDetails";
import { MarkerPhotos } from "./models/MarkerPhotos";
import { GeoCodingID, GeocodingResult } from "./models/GeoCoding";
import { MapParams } from "./models/MapParams";
import { MenuItem, RouterReadyEvent } from "@abaco/web-common/src/app";
import { createMenu } from "./createMenu";
import { propsToDate } from "@abaco/web-common/src/HTTP";
import FarmerRegistryModule from "@abaco/farm-registry/src/modules/farm-registry";
import axios, { CancelTokenSource } from "axios";

export const MODULE_ID = "geo_tagging";
const BASEPATH = "/geotag";

export default class GeoTaggingModule implements AbacoWebModule {
    static MODULE_ID = MODULE_ID;
    private http!: HTTPClient;
    private cancelTokenSource: CancelTokenSource | null = null;

    readonly id = MODULE_ID;
    readonly routes = myRoutes;
    readonly additionalI18N = ["web_components"];
    readonly globalComponents = globals;
    readonly vuexModule = new GeoTaggingVuexModule();
    menu: MenuItem[] = [];

    dependsOn = {
        [FarmerRegistryModule.MODULE_ID]: () => new FarmerRegistryModule(),
    };

    onRouterReady(evt: RouterReadyEvent) {
        this.menu = createMenu(evt.router);
    }

    onApplicationReady(event: ApplicationReadyEvent) {
        this.http = event.http;
    }

    public async searchBusiness(
        filter: string | null,
        pp: PagingParams
    ): Promise<PagedResults<Business>> {
        // if (this.cancelTokenSource) {
        //     this.cancelTokenSource.cancel();
        // }
        // this.cancelTokenSource = axios.CancelToken.source();
        const filterUpper = filter ? filter.toUpperCase() : undefined;
        const result = await this.http.get(
            BASEPATH + "/api/v1/registry/paged",
            {
                params: {
                    filter: filterUpper,
                    offset: pp.offset,
                    count: pp.count,
                },
            }
        );
        // cancelToken: this.cancelTokenSource.token,
        return result.data;
    }

    public async searchBusinessV2(
        filter: string | null,
        pp: PagingParams
    ): Promise<PagedResults<Business>> {
        const filterUpper = filter ? filter.toUpperCase() : undefined;
        const result = await this.http.get(
            BASEPATH + "/api/v1/registry/configured/paged",
            {
                params: {
                    filter: filterUpper,
                    offset: pp.offset,
                    count: pp.count,
                },
            }
        );
        return result.data;
    }

    public async getCategories(): Promise<MarkerCategories[]> {
        const result = await this.http.get(
            BASEPATH + "/api/v1/geophotoconfig/categories"
        );
        return result.data;
    }

    public async checkGeophotoPermission(permission: string, pkCuaa: number) {
        const res = await this.http.get(
            `/sitiagri-rest-api/api_basic/v1/user_registry/subjects/${pkCuaa}/module_competencies/GEOPHOTO/${permission}`
        );
        return res.data.result;
    }

    public async getMarker(marker: string | null): Promise<MarkerDetails> {
        const result = await this.http.get(
            BASEPATH + "/api/v1/markers/" + marker
        );
        propsToDate(result.data, ["dt_start"]);
        return result.data;
    }

    public async getMarkerPhotos(
        marker: string | null
    ): Promise<MarkerPhotos[]> {
        const result = await this.http.get(BASEPATH + "/api/v1/photos", {
            params: { marker_id: marker },
        });
        return result.data;
    }

    public async setMarkerVerify(marker: string | null) {
        await this.http.put(BASEPATH + "/api/v1/markers/" + marker);
    }
    public async cancelMarker(marker: string | null) {
        const status = "CANCELLED";
        const url = `${BASEPATH}/api/v1/markers/${marker}?status=${status}`;
        await this.http.put(url);
    }

    public async getPhoto(
        photoId: number,
        maxHeight: number | null
    ): Promise<string> {
        const result = await this.http
            .get(
                BASEPATH +
                    "/api/v1/photos/" +
                    photoId +
                    "/b64" +
                    (maxHeight != null ? "?maxHeight=" + maxHeight : "")
            )
            .catch(() => {
                return null;
            });
        if (result != null) return result.data;

        return "";
    }

    public async searchMarkers(
        business: Business | null,
        filters: SearchMarkerFilters
    ): Promise<SearchMarkerResult[]> {
        const result = await this.http.get(BASEPATH + "/api/v1/markers", {
            params: {
                pk_cuaa:
                    business != null && business.business_id != null
                        ? business.business_id
                        : "",
                id_category:
                    filters.idCategory != null ? filters.idCategory : "",
                status: filters.status != null ? filters.status : "",
                due_date:
                    filters.dateFilter != null
                        ? filters.dateFilter.toISOString()
                        : "",
                reference_id:
                    filters.idReference != null
                        ? filters.idReference.toUpperCase()
                        : "",
                description:
                    filters.description != null
                        ? filters.description.toUpperCase()
                        : "",
                code:
                    filters.markerCode != null
                        ? filters.markerCode.toUpperCase()
                        : "",
                user_assigned:
                    filters.assignedTo != null
                        ? filters.assignedTo.toUpperCase()
                        : "",
                date_type: filters.dateType != null ? filters.dateType : "",
            },
        });
        return result.data;
    }

    public async searchMarkersPaged(
        business: Business | null,
        filters: SearchMarkerFilters,
        pp: PagingParams
    ): Promise<PagedResults<SearchMarkerResult>> {
        if (filters.dateFilter != null) {
            filters.dateFilter.setHours(10);
        }
        if (filters.uploadDateFilter != null) {
            filters.uploadDateFilter.setHours(10);
        }
        const result = await this.http.get(BASEPATH + "/api/v1/markers/paged", {
            params: {
                pk_cuaa:
                    business != null && business.business_id != null
                        ? business.business_id
                        : "",
                id_category:
                    filters.idCategory != null ? filters.idCategory : "",
                status: filters.status != null ? filters.status : "",
                due_date:
                    filters.dateFilter != null
                        ? filters.dateFilter.toISOString()
                        : "",
                upload_date:
                    filters.uploadDateFilter != null
                        ? filters.uploadDateFilter.toISOString()
                        : "",
                reference_id:
                    filters.idReference != null
                        ? filters.idReference.toUpperCase()
                        : "",
                description:
                    filters.description != null
                        ? filters.description.toUpperCase()
                        : "",
                code:
                    filters.markerCode != null
                        ? filters.markerCode.toUpperCase()
                        : "",
                date_type: filters.dateType != null ? filters.dateType : "",
                upload_type:
                    filters.uploadDateType != null
                        ? filters.uploadDateType
                        : "",
                user_assigned:
                    filters.assignedTo != null
                        ? filters.assignedTo.toUpperCase()
                        : "",
                locality: filters.locality != null ? filters.locality : "",
                offset: pp.offset,
                limit: pp.count,
            },
        });
        propsToDatePaged(result.data, ["due_date"]);
        return result.data;
    }

    // public async getGPToken(userid: string, farmerid: string | null): Promise<string>
    // {
    //     const result = await this.http.get(BASEPATH + "/api/v1/registry/gptoken", {params:{"userid": userid, "farmerid": farmerid}});
    //     return result.data;
    // }

    public async insertMarker(
        businessCode: string | null,
        businessName: string | null,
        markerCode: string | null,
        referenceId: string | null,
        category: string | null,
        dueDate: Date,
        /*dueDateMessage: string | null, */ markerTitle: string | null,
        markerDescription:
            | string
            | null /*conversationTitle: string | null, infoMessage: string | null, */,
        latLon: string | null,
        range: number | null,
        epsgCode: string | null,
        markerObjectName: string | null,
        locality: string | null /*max_distance: number*/
    ): Promise<string> {
        let retval = true;
        let sRet = "";
        const a = await this.http
            .post(BASEPATH + "/api/v1/markers/insert", {
                business_code: businessCode,
                business_name: businessName,
                marker_code: markerCode,
                reference_id: referenceId,
                category: category,
                due_date: new Date(
                    dueDate.setMinutes(
                        dueDate.getMinutes() - dueDate.getTimezoneOffset()
                    )
                ) /*"due_date_message": dueDateMessage, */,
                marker_title: markerTitle,
                marker_description:
                    markerDescription /*"conversation_title": conversationTitle, "info_message": infoMessage,*/,
                lat_lon: latLon,
                epsg_code: epsgCode,
                range: range /*"max_distince": max_distince*/,
                object_name: markerObjectName,
                locality: locality,
            })
            .catch((error) => {
                retval = false;
                sRet = error.response.data.message;
            });
        return sRet;
    }

    // public async getCapability(token: string): Promise<string>
    // {
    //     const result = await this.http.get("/t/wmts?REQUEST=GetCapabilities&gptoken=" + token);
    //     return result.data;
    // }

    public async getCapabilityXml(farmerid: string | null): Promise<string> {
        const result = await this.http.get(
            BASEPATH + "/api/v1/map/getWMTSCapab",
            { params: { farmerid: farmerid } }
        );
        return result.data;
    }

    // public async getWMTSLayerName(): Promise<string>
    // {
    //     const result = await this.http.get(BASEPATH + "/api/v1/map/getWMTSLayer");
    //     return result.data;
    // }

    public async getWMTSParams(farmerid: string | null): Promise<MapParams> {
        const result = await this.http.get(
            BASEPATH + "/api/v1/map/getWMTSParams",
            { params: { farmerid: farmerid } }
        );
        return result.data;
    }

    public async getGeoCodingResults(token: string): Promise<GeoCodingID[]> {
        const result = await this.http.get(
            "/geocoding-api/api/v1/geocoding/results?search=" + token
        );
        return result.data;
    }

    public async callGeoCoding(id: string): Promise<GeocodingResult> {
        console.log("2");
        const result = await this.http.get(
            "/geocoding-api/api/v1/geocoding/results/" + id
        );
        return result.data;
    }
}
